import React from "react";
import {
  Close as CloseIcon,
  Share,
  LocationOn,
  WhatsApp,
  Facebook,
  ContentCopy,
  Beenhere,
} from "@mui/icons-material";
import {
  IconButton,
  Typography,
  Tooltip,
  Button,
  Dialog,
  DialogContent,
} from "@mui/material";
import { toast } from "react-toastify";
import { useMapEvents } from "react-leaflet";
import { coordsIntoCode } from "utils/coordinatesConvert";

const NewMapPopup = ({
  location,
  onClose,
  isCurrentLocation,
  handleFillPress,
}) => {
  const [addressCode, setAddressCode] = React.useState("");
  const [shareDialogOpen, setShareDialogOpen] = React.useState(false);

  const map = useMapEvents({});

  const handleShare = () => setShareDialogOpen(true);

  const handleCloseShareDialog = () => setShareDialogOpen(false);

  React.useEffect(() => {
    const coordinates = {
      lat: location.lat.toFixed(6),
      lng: location.lng.toFixed(6),
    };
    setAddressCode(coordsIntoCode(coordinates));
  }, [location]);

  const handleCopyCoordinates = () => {
    const coordinates = {
      lat: location.lat.toFixed(6),
      lng: location.lng.toFixed(6),
    };

    const addressCode = `${coordinates.lat}, ${coordinates.lng}`;

    navigator.clipboard
      .writeText(addressCode)
      .then(() => {
        toast.success("Coordinates copied to clipboard!");
      })
      .catch(() => {
        toast.error("Failed to copy!");
      });
  };

  const handleCopyCode = () => {
    const coordinates = {
      lat: location.lat.toFixed(6),
      lng: location.lng.toFixed(6),
    };

    const addressCode = coordsIntoCode(coordinates);

    navigator.clipboard
      .writeText(addressCode)
      .then(() => {
        toast.success("Code copied to clipboard!");
      })
      .catch(() => {
        toast.error("Failed to copy!");
      });
  };

  const handleGetDirection = () => {
    const coordinates = {
      lat: location.lat.toFixed(6),
      lng: location.lng.toFixed(6),
    };
    setAddressCode(coordsIntoCode(coordinates));
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${coordinates.lat},${coordinates.lng}`,
      "_blank"
    );
  };

  const shareOnWhatsApp = () => {
    const coordinates = {
      lat: location.lat.toFixed(6),
      lng: location.lng.toFixed(6),
    };
    setAddressCode(coordsIntoCode(coordinates));
    const message = `Check out this location: https://maps.google.com/?q=${coordinates.lat},${coordinates.lng}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  const shareOnFacebook = () => {
    const coordinates = {
      lat: location.lat.toFixed(6),
      lng: location.lng.toFixed(6),
    };
    setAddressCode(coordsIntoCode(coordinates));
    const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=https://maps.google.com/?q=${coordinates.lat},${coordinates.lng}`;
    window.open(fbShareUrl, "_blank");
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else if (isCurrentLocation) {
      map?.setView([location?.lat, location?.lng], 16);
      map?.eachLayer((layer) => {
        if (layer.closePopup) {
          layer.closePopup();
        }
      });
    } else {
      map?.closePopup();
    }
  };

  return (
    <>
      <>
        <div className="mb-1 flex items-center justify-between">
          <Typography
            variant="subtitle2"
            className="flex items-center gap-1 text-blue-700"
          >
            <LocationOn fontSize="small" />
            {isCurrentLocation ? "Current Location" : "Selected Location"}
          </Typography>
          <Tooltip title="Close">
            <IconButton
              className="text-blue-200 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-100"
              size="small"
              onClick={handleClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        <Typography
          variant="body2"
          className="mb-2 font-mono text-gray-600"
          style={{ lineHeight: "1.2" }}
        >
          Coordinates: {location?.lat?.toFixed(6)}, {location?.lng?.toFixed(6)}
        </Typography>

        <Typography
          variant="body2"
          className="mb-2 font-mono text-gray-600"
          style={{ lineHeight: "1.2" }}
        >
          Code: {addressCode}
        </Typography>

        <div className="flex justify-between gap-3">
          <Tooltip title={"Fill address coordinates and code"}>
            <Button
              className="text-blue-200 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-100"
              size="small"
              startIcon={<Beenhere fontSize="inherit" />}
              onClick={() => handleFillPress(true)}
            >
              Fill In Form
            </Button>
          </Tooltip>
          <Tooltip title={"Share Address"}>
            <Button
              onClick={handleShare}
              className="text-blue-200 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-100"
              size="small"
              startIcon={<Share fontSize="inherit" />}
            >
              Share
            </Button>
          </Tooltip>
        </div>
      </>

      <Dialog
        open={shareDialogOpen}
        PaperProps={{
          className: "max-w-sm w-full m-4",
        }}
      >
        <div className="flex items-center justify-between border-b p-3">
          <Typography className="font-large text-base">
            Share Address
          </Typography>
          <Tooltip title={"Close"}>
            <IconButton
              onClick={handleCloseShareDialog}
              className="text-blue-200 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-100"
              size="small"
            >
              <CloseIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
        </div>

        <DialogContent className="p-2">
          <div className="flex flex-col">
            <div className="flex gap-6">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Typography className="text-sm text-gray-600">
                    Code: {addressCode}
                  </Typography>
                  <Tooltip title="Copy Code">
                    <IconButton
                      onClick={handleCopyCode}
                      size="small"
                      className="p-0"
                    >
                      <ContentCopy sx={{ fontSize: 16, color: "#6366F1" }} />
                    </IconButton>
                  </Tooltip>
                </div>

                <div className="flex items-center gap-2">
                  <Typography className="text-sm text-gray-600">
                    Coordinates: {location.lat.toFixed(6)},{" "}
                    {location.lng.toFixed(6)}
                  </Typography>
                  <Tooltip title="Copy Coordinates">
                    <IconButton
                      onClick={handleCopyCoordinates}
                      size="small"
                      className="p-0"
                    >
                      <ContentCopy sx={{ fontSize: 16, color: "#6366F1" }} />
                    </IconButton>
                  </Tooltip>
                </div>

                <button
                  onClick={handleGetDirection}
                  className="mt-1 text-left text-sm text-blue-600 hover:underline"
                >
                  Get Direction
                </button>
                <div className="flex items-center gap-4">
                  <Typography className="text-sm text-gray-600">
                    Share on:
                  </Typography>
                  <div className="flex gap-2">
                    <Tooltip title="Share On WhatsApp">
                      <IconButton
                        onClick={shareOnWhatsApp}
                        className="p-1 hover:bg-gray-100"
                        size="small"
                      >
                        <WhatsApp sx={{ fontSize: 22, color: "#25D366" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Share On Facebook">
                      <IconButton
                        onClick={shareOnFacebook}
                        className="p-1 hover:bg-gray-100"
                        size="small"
                      >
                        <Facebook sx={{ fontSize: 22, color: "#1877F2" }} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NewMapPopup;
